<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import { useI18n } from 'vue-i18n'
import { BuildingStorefrontIcon, PencilSquareIcon } from '@heroicons/vue/24/outline'
import type { BpProduct, Tab } from '~/definitions/AllDefinitions'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import { useProductStore } from '~/store/ProductStore'
import { isLast } from '~/classes/Utils'
import BpFooter from '~/components/Navigation/BpFooter.vue'
import Cart from '~/components/Cart/Cart.vue'
import DeliveryMethodSelector from '~/components/DeliveryMethod/DeliveryMethodSelector.vue'
import LocationAvailability
  from '~/components/Location/LocationAvailability.vue'
import LocationSelectorModal from '~/components/Location/LocationSelectorModal.vue'
import Navbar from '~/components/Navigation/Navbar.vue'
import Product from '~/components/Product/Product.vue'
import ProductAvailability from '~/components/Product/ProductAvailability.vue'
import ProductInfo from '~/components/Product/ProductInfo.vue'
import TwTabs from '~/components/TwTabs.vue'

definePageMeta({
  middleware: ['get-root-data', 'set-color-mode', 'get-shop-categories', 'get-structured-data'],
  layout: 'default',
})

const { t } = useI18n()
const mainStore = useMainStore()
const cartStore = useCartStore()
const productStore = useProductStore()
const { categories } = storeToRefs(productStore)
const { currentLocation, currentLocations, rootData } = storeToRefs(mainStore)
const { cartOpen } = storeToRefs(cartStore)

// reactive properties
const currentProduct = ref(null as BpProduct | null)
const displayProduct = ref(false)
const deliveryMethodsIsOpen = ref(false)
const locationSelectorIsOpen = ref(false)
const loading = ref(false)
const searchText = ref('')
const tabs = ref([] as Tab[])

// computed properties
const mapURL = computed(() => {
  if (currentLocation.value?.formattedAddress) {
    const encodedAddress = encodeURI(currentLocation.value.formattedAddress)
    return `https://www.google.com/maps/place/${encodedAddress}`
  }
  return ''
})
const visibleItems = computed(() => {
  let res = [...categories.value]
  if (searchText.value && searchText.value.length) {
    const search = searchText.value.toLowerCase()
    const cats = []
    if (categories.value.length) {
      for (const k in categories.value) {
        const cat = cloneDeep(categories.value[k])
        const catName = cat.name ? cat.name.toLowerCase() : null
        const catDesc = cat.description ? cat.description.toLowerCase() : null
        if (
          (catName && catName.includes(search)) ||
          (catDesc && catDesc.includes(search))
        ) {
          cats.push(cat)
        } else {
          const catItems: BpProduct[] = cloneDeep((cat.products || []))
          const subItems: BpProduct[] = []
          for (const j in catItems) {
            const subItem: BpProduct = { ...catItems[j] }
            const name = subItem.name ? subItem.name.toLowerCase() : null
            const desc = subItem.description
              ? subItem.description.toLowerCase()
              : null
            if (
              (name && name.includes(search)) ||
              (desc && desc.includes(search))
            ) {
              subItems.push(subItem)
            } else if (subItem.productModifierGroups?.length) {
              const allModifiers = subItem.productModifierGroups.flatMap(group => group.modifiers)
              for (const l in allModifiers) {
                const opt = { ...allModifiers[l] }
                const optName = opt.name
                  ? opt.name.toLowerCase()
                  : null
                if (optName && optName.includes(search)) {
                  subItems.push(subItem)
                }
              }
            }
          }
          if (subItems.length) {
            cat.products = [...subItems]
            cats.push(cat)
          }
        }
      }
    }
    res = [...cats]
  }
  return res.filter(cat => cat.archived === false)
})
const hasShopCategories = computed(() => {
  return visibleItems.value.length > 0
})

// methods
function closeProductInfo() {
  currentProduct.value = null
  displayProduct.value = false
}
function buildTabs() {
  tabs.value.length = 0
  let counter = 0
  categories.value.forEach((category) => {
    if (category.type === 'grouped' && category.name) {
      tabs.value.push({
        current: counter === 0,
        name: category.name,
        value: `category-${category.uuid}`,
      })
      counter++
    }
  })
}
async function getShopCategories() {
  loading.value = true
  await productStore.getShopCategories()
  buildTabs()
  loading.value = false
}
function handleCloseDeliveryMethods() {
  deliveryMethodsIsOpen.value = false
}
function handleOpenDeliveryMethods() {
  deliveryMethodsIsOpen.value = true
}
function handleSearchInput(val?: string) {
  if (val) {
    searchText.value = clone(val)
  } else {
    searchText.value = ''
  }
}
function handleToggleProduct() {
  if (displayProduct.value) {
    closeProductInfo()
  } else {
    displayProduct.value = true
  }
}
function handleViewProduct(product: BpProduct) {
  if (currentLocations.value && currentLocations.value.length > 1 && !currentLocation.value?.id) {
    handleLocationRequired()
    return
  }
  currentProduct.value = product
  if (!displayProduct.value) {
    displayProduct.value = true
  }
}
function handleLocationRequired() {
  // Open location selector when user tries to add to cart without a location selected
  locationSelectorIsOpen.value = true
}
function handleCloseLocationSelector() {
  locationSelectorIsOpen.value = false
}
async function handleLocationSelected() {
  locationSelectorIsOpen.value = false
  // Refresh shop categories with selected location
  await getShopCategories()
}

// Build tabs immediately — categories are already loaded from middleware
buildTabs()

onMounted(() => {
  closeProductInfo()
  if (!mainStore.locationId && rootData.value.locations.length === 1) {
    mainStore.getLocation(rootData.value.locations[0]?.id || 0)
  } else if (mainStore.locationId) {
    mainStore.getLocation(mainStore.locationId)
  }
})
</script>

<template>
  <div
    class="max-h-full h-full max-w-full w-full flex flex-col overflow-hidden
    basis-full"
  >
    <navbar
      :display-delivery-methods="false"
      :display-search="false"
      class="z-20 w-full"
      @open-delivery-methods="handleOpenDeliveryMethods"
      @search-input="handleSearchInput"
    />
    <div
      class="flex flex-col w-full relative bg-tw-1 overflow-y-scroll basis-full"
    >
      <div class="w-full bg-tw-2 text-tw-1 sm:py-5 py-2.5">
        <div class="tw-container sm:text-sm text-xs">
          <div
            class="
              w-full
              flex
              flex-row
              items-center
              sm:justify-between
              justify-center
              flex-wrap
            "
          >
            <div class="inline-flex sm:w-auto w-full items-center">
              <BuildingStorefrontIcon
                :aria-hidden="true"
                class="h-5 w-5 mr-2 hidden sm:inline-flex"
              />
              <div
                v-if="!!(currentLocation?.id)"
                class="sm:inline-flex block grow"
              >
                <div>
                  <a
                    :href="mapURL"
                    target="_blank"
                  >
                    {{ currentLocation.formattedAddress }}
                  </a>
                </div>
                <a
                  v-if="currentLocation.phoneFormatted"
                  :href="`tel:${currentLocation.phoneUnformatted}`"
                >
                  <span class="hidden sm:inline-flex">&nbsp;&bull;&nbsp;</span>{{ currentLocation.phoneFormatted }}
                </a>
              </div>
              <div
                v-else
                class="flex justify-center flex-1 items-center"
              >
                {{ t('ORDER_DIALOG_HEADLINE') }}
                <button
                  type="button"
                  class="ml-4"
                  @click="handleLocationRequired"
                >
                  <PencilSquareIcon class="w-5 h-5 text-tw-1 hover:text-tw-1" />
                </button>
              </div>
            </div>
            <div
              v-if="!!(currentLocation?.id)"
              class="sm:w-auto w-full flex flex-1 sm:flex-initial items-center"
            >
              <location-availability />
              <button
                v-if="false"
                type="button"
                class="ml-4"
                @click="handleLocationRequired"
              >
                <PencilSquareIcon class="w-5 h-5 text-tw-1 hover:text-tw-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full mb-10 flex-grow basis-full">
        <div class="tw-container text-slate-700 dark:text-white">
          <div
            v-if="loading"
            class="w-full my-5"
          >
            <div class="w-full sm:grid sm:grid-cols-2 sm:gap-5 mb-5">
              <div class="bg-tw-2 rounded-md animate-pulse sm:col-auto w-full h-16" />
              <div class="bg-tw-2 rounded-md animate-pulse sm:col-auto w-full h-16 sm:block hidden" />
            </div>
            <div
              v-for="idx in 4"
              :key="idx"
              :class="{ 'border-b border-tw-2 pb-5 mb-5': idx < 4 }"
            >
              <div class="sm:w-[25%] w-[50%] rounded-md h-8 bg-tw-2 animate-pulse mb-5" />
              <div class="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 xl:gap-x-8 w-full">
                <div
                  v-for="item in 4"
                  :key="item"
                  class="group cursor-pointer w-full"
                >
                  <div class="grid sm:grid-cols-1 grid-cols-12 gap-5">
                    <div class="sm:col-auto col-span-3">
                      <div class="rounded-lg aspect-square w-full bg-tw-2 animate-pulse" />
                    </div>
                    <div class="sm:col-auto col-span-9">
                      <div class="w-[80%] rounded-md bg-tw-2 h-8 animate-pulse mb-2" />
                      <div class="w-[90%] rounded-md bg-tw-2 h-8 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            :class="{
              'mt-10': !(tabs && tabs.length > 1),
              'mt-5': tabs && tabs.length > 1,
            }"
            class="w-full sm:mt-3 relative"
          >
            <div v-if="tabs && tabs.length > 1">
              <tw-tabs
                :label="t('CATEGORIES')"
                :tabs="tabs"
              />
            </div>
            <div
              v-if="hasShopCategories"
              class="w-full my-5"
            >
              <div
                v-for="(category, categoryIdx) in visibleItems"
                :key="category.uuid"
                class="category-container"
                :class="{ 'border-b border-tw-2 pb-5 mb-5': !isLast(visibleItems, +categoryIdx) }"
              >
                <div>
                  <h2
                    v-if="category.type === 'grouped' && category.name"
                    :id="`category-${category.uuid}`"
                    class="text-2xl font-bold tracking-tight text-tw-1"
                  >
                    {{ category.name }}
                  </h2>
                  <p
                    v-if="category.description"
                    class="font-normal text-sm text-slate-500 dark:text-slate-400"
                  >
                    {{ category.description }}
                  </p>
                  <product-availability
                    v-if="!category.availability.default && category.availability.hours"
                    :hours="category.availability.hours"
                    class="font-normal text-sm"
                  />
                </div>
                <div
                  v-if="category.products && category.products.length"
                  class="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2
                  xl:grid-cols-5 lg:grid-cols-4 xl:gap-x-8 w-full"
                  :class="{ 'mt-5': category.type === 'grouped' }"
                >
                  <product
                    v-for="product in category.products"
                    :key="product.uuid"
                    :product="product"
                    :single="category.type !== 'grouped'"
                    class="w-full"
                    @view-product="handleViewProduct"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              class="w-full my-5"
            >
              <div class="w-full text-center">
                {{ t('NO_X_FOUND_FILTER', { value: t('PRODUCTS').toLowerCase() }) }}
              </div>
            </div>
          </div>
        </div>
        <ClientOnly>
          <product-info
            v-if="!loading"
            :open="displayProduct"
            :initial-product="currentProduct"
            @toggle="handleToggleProduct"
            @location-required="handleLocationRequired"
          />
          <cart
            v-if="!loading"
            :open="cartOpen"
          />
          <delivery-method-selector
            v-if="!loading"
            :enable-time-selector="false"
            :open="deliveryMethodsIsOpen"
            @close="handleCloseDeliveryMethods"
          />
          <location-selector-modal
            v-if="!loading && rootData.locations.length > 1"
            :open="locationSelectorIsOpen"
            @close="handleCloseLocationSelector"
            @location-selected="handleLocationSelected"
          />
        </ClientOnly>
      </div>
      <bp-footer />
    </div>
  </div>
</template>
